import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { retryWhen, mergeMap } from 'rxjs/operators';

import { UrlService } from '@core/services/url/url.service';
import { AmsFile } from '@core/objects/ams-file';

/**
 * Storage preview page response from API
 */
export interface StoragePreviewImage {
    url: string;
    expires_at: string;
}

export interface StoragePreviewPageResponse {
    position: number;
    image: StoragePreviewImage;
}

/**
 * Storage preview response from API
 * Returns an array of pages with presigned URLs for each page
 */
export interface StoragePreviewResponse {
    uuid: string;
    pages: StoragePreviewPageResponse[];
}

/**
 * API error response structure
 */
export interface StorageApiError {
    code?: string;
    message?: string;
    status?: number;
}

/**
 * StoragePreviewsService
 * 
 * Handles fetching and managing preview pages from the Storage API.
 * Features:
 * - Fetches preview pages with presigned URLs from the Storage API
 * - Implements retry logic for 425 (Too Early) responses (10 retries, 3 seconds delay = 30 seconds total)
 * - Manages URL expiration tracking (URLs expire after 5 minutes)
 * - Provides utility methods for expiration checking
 * 
 * API Endpoint: GET /pub/p/{uuid}/img?signature={signature}&size={size}
 * 
 * Response contains array of pages with presigned URLs:
 * {
 *   "uuid": "...",
 *   "pages": [
 *     {
 *       "position": 1,
 *       "image": {
 *         "url": "https://download.twonas.com/previews/...",
 *         "expires_at": "2026-02-17T15:30:00+00:00"
 *       }
 *     }
 *   ]
 * }
 */
@Injectable({
    providedIn: 'root'
})
export class StoragePreviewsService {
    /**
     * Maximum number of retries for 425 errors (Too Early - preview generation in progress)
     */
    private readonly MAX_RETRIES = 10;

    /**
     * Delay between retries in milliseconds (3 seconds)
     * Total retry time: 10 attempts × 3 seconds = 30 seconds
     */
    private readonly RETRY_DELAY_MS = 3000;

    /**
     * Time before expiration to trigger refresh (30 seconds before expiry)
     * This ensures we get new URLs before the current ones expire
     */
    private readonly REFRESH_BEFORE_EXPIRY_MS = 30000;

    constructor(
        private http: HttpClient,
        private url: UrlService
    ) {}

    /**
     * Fetches the list of available preview pages from the Storage API
     * 
     * Implements retry logic for 425 (Too Early) responses:
     * - Up to 10 retries with 3 second delay between attempts
     * - Total maximum wait time: 30 seconds
     * - If still not ready after 10 retries, returns error
     * 
     * @param file The AmsFile containing uuid and access signature
     * @param size Image preview quality/resolution
     * @returns Observable<StoragePreviewResponse>
     */
    getPreviewPages(file: AmsFile, size: 'min' | 'small' | 'medium' | 'big' = 'small'): Observable<StoragePreviewResponse> {
        if (!file?.uuid || !file?.access?.signature) {
            return throwError(() => new Error('Invalid file or missing signature'));
        }

        let retryAttempt = 0;
        const apiUrl = this.buildPreviewListUrl(file, size);

        return this.http.get<StoragePreviewResponse>(apiUrl).pipe(
            retryWhen(errors =>
                errors.pipe(
                    mergeMap(error => {
                        // Check if this is a 404 with preview_images_not_exists code (file has no preview)
                        const errorCode = error.error?.code || error.error?.error?.code;
                        if (error.status === 404 && errorCode === 'preview_images_not_exists') {
                            // File has no preview (e.g., zip, tar) - don't retry, return immediately
                            return throwError(() => error);
                        }

                        // Only retry on 425 (Too Early) status
                        if (error.status === 425 && retryAttempt < this.MAX_RETRIES) {
                            retryAttempt++;
                            // Retry after delay
                            return timer(this.RETRY_DELAY_MS);
                        }
                        // For other errors or max retries reached, throw the error
                        return throwError(() => error);
                    })
                )
            )
        );
    }

    /**
     * Builds the URL for fetching preview page list from Storage API
     * 
     * @param file The AmsFile containing uuid and access signature
     * @param size Image preview quality/resolution
     * @returns The complete API URL with query parameters
     */
    private buildPreviewListUrl(file: AmsFile, size: string): string {
        const baseUrl = this.url.buildFiles(`/pub/p/${file.uuid}/img`);
        const params = new URLSearchParams();
        params.append('signature', file.access.signature);
        params.append('size', size);
        return `${baseUrl}?${params.toString()}`;
    }

    /**
     * Check if a presigned URL has expired
     * 
     * @param expiresAt ISO 8601 formatted expiration timestamp
     * @returns true if URL has expired, false otherwise
     */
    isExpired(expiresAt: string): boolean {
        try {
            const expirationTime = new Date(expiresAt).getTime();
            const currentTime = new Date().getTime();
            return currentTime >= expirationTime;
        } catch (e) {
            console.warn('[StoragePreviewsService] Invalid expiration date format:', expiresAt);
            return false;
        }
    }

    /**
     * Get milliseconds until expiration for a given timestamp
     * 
     * @param expiresAt ISO 8601 formatted expiration timestamp
     * @returns Number of milliseconds until expiration (negative if already expired)
     */
    getTimeUntilExpiration(expiresAt: string): number {
        try {
            const expirationTime = new Date(expiresAt).getTime();
            const currentTime = new Date().getTime();
            return expirationTime - currentTime;
        } catch (e) {
            console.warn('[StoragePreviewsService] Invalid expiration date format:', expiresAt);
            return 0;
        }
    }

    /**
     * Get the time until refresh should occur (before actual expiration)
     * 
     * @param expiresAt ISO 8601 formatted expiration timestamp
     * @returns Number of milliseconds until refresh should occur
     */
    getTimeUntilRefresh(expiresAt: string): number {
        const timeUntilExpiry = this.getTimeUntilExpiration(expiresAt);
        // Refresh when we're within REFRESH_BEFORE_EXPIRY_MS of expiration
        return timeUntilExpiry - this.REFRESH_BEFORE_EXPIRY_MS;
    }

    /**
     * Get the earliest expiration time from a list of preview pages
     * 
     * @param pages Array of StoragePreviewPageResponse
     * @returns ISO 8601 formatted expiration timestamp of the earliest expiring page
     */
    getEarliestExpiration(pages: StoragePreviewPageResponse[]): string {
        if (!pages || pages.length === 0) {
            return null;
        }

        return pages.reduce((earliest, page) => {
            const currentTime = new Date(page.image.expires_at).getTime();
            const earliestTime = new Date(earliest).getTime();
            return currentTime < earliestTime ? page.image.expires_at : earliest;
        }, pages[0].image.expires_at);
    }

    /**
     * Check if any page in the preview list has expired
     * 
     * @param pages Array of StoragePreviewPageResponse
     * @returns true if any page has expired, false otherwise
     */
    anyPageExpired(pages: StoragePreviewPageResponse[]): boolean {
        if (!pages || pages.length === 0) {
            return false;
        }

        return pages.some(page => this.isExpired(page.image.expires_at));
    }

    /**
     * Check if any page needs refresh (is approaching expiration)
     * 
     * @param pages Array of StoragePreviewPageResponse
     * @returns true if any page should be refreshed soon, false otherwise
     */
    anyPageNeedsRefresh(pages: StoragePreviewPageResponse[]): boolean {
        if (!pages || pages.length === 0) {
            return false;
        }

        return pages.some(page => {
            const timeUntilRefresh = this.getTimeUntilRefresh(page.image.expires_at);
            return timeUntilRefresh <= 0;
        });
    }
}
